import moment from 'moment-timezone';

export const getFirstBillingValue = productsFinancingData => {
  // 1+G/1+i
  return productsFinancingData.reduce((prev, current) => {
    const { interestRate, billings, totalFinancingValue } = current;

    const denominator = Math.pow(1 + interestRate / 100, billings);
    const numerator = Math.pow(1 + interestRate / 100, billings);

    const wholeOperation =
      denominator - 1
        ? totalFinancingValue *
          ((numerator * interestRate) / 100 / (denominator - 1))
        : 0;

    return prev + Math.trunc(wholeOperation);
  }, 0);
};

const normalizeBillings = billings => {
  const parsedBillings = Number.parseInt(billings, 10);

  return Number.isFinite(parsedBillings) && parsedBillings > 0
    ? parsedBillings
    : 0;
};

export const getPaymentPlanDetail = productsFinancingData => {
  if (!productsFinancingData || productsFinancingData.length === 0) {
    return {
      totalInterest: 0,
      totalCapital: 0,
      totalPayment: 0,
      rows: []
    };
  }

  const totalBillings = productsFinancingData.reduce((maxBillings, product) => {
    const billings = normalizeBillings(product && product.billings);

    return billings > maxBillings ? billings : maxBillings;
  }, 0);

  const products = productsFinancingData.map(product => {
    const now = moment().tz('America/Bogota');
    const { interestRate, totalFinancingValue } = product;
    const billings = normalizeBillings(product && product.billings);
    let totalCapital = 0;
    let totalInterest = 0;
    let pendingBalance = totalFinancingValue;

    const rows = [];
    const monthlyInstallment = getFirstBillingValue([
      { interestRate, billings, totalFinancingValue }
    ]);

    for (let i = 0; i < billings; i++) {
      const interest = Math.floor(pendingBalance * (interestRate / 100));
      totalInterest = interest + totalInterest;

      const capital = monthlyInstallment - interest;
      totalCapital = capital + totalCapital;

      pendingBalance = Math.floor(pendingBalance - capital);

      rows.push({
        period: now.add(1, 'months').format('YYYY - MMM'),
        capital,
        interest,
        total: monthlyInstallment,
        pendingBalance
      });
    }
    if (billings > 0) {
      rows[billings - 1].capital += rows[billings - 1].pendingBalance;
      rows[billings - 1].total += rows[billings - 1].pendingBalance;
      totalCapital += rows[billings - 1].pendingBalance;
      rows[billings - 1].pendingBalance = 0;
    }
    return {
      rows,
      totalInterest,
      totalCapital,
      totalPayment: totalCapital + totalInterest
    };
  });

  const emptyRow = { capital: 0, interest: 0, total: 0, pendingBalance: 0 };

  return products.reduce(
    (prev, current) => {
      const rows = new Array(totalBillings).fill(null).map((_, idx) => {
        const prevRow = prev.rows[idx] || emptyRow;
        const row = (current.rows && current.rows[idx]) || emptyRow;

        return {
          ...row,
          capital: prevRow.capital + (row.capital ? row.capital : 0),
          interest: prevRow.interest + (row.interest ? row.interest : 0),
          total: prevRow.total + (row.total ? row.total : 0),
          pendingBalance: prevRow.pendingBalance + (row.pendingBalance || 0)
        };
      });

      return {
        totalInterest: prev.totalInterest + current.totalInterest,
        totalCapital: prev.totalCapital + current.totalCapital,
        totalPayment: prev.totalPayment + current.totalPayment,
        rows
      };
    },
    {
      totalInterest: 0,
      totalCapital: 0,
      totalPayment: 0,
      rows: new Array(totalBillings).fill(emptyRow)
    }
  );
};
